// @ts-strict-ignore
import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import { Pressable } from 'react-native-web';
import analytics from '../../../../modules/analytics';
import {
  MultiFactorAuthMethod,
  MultiFactorAuthChoiceType,
  MultiFactorAuthStatus,
} from './types';
import MultiFactorAuthMethodForm from './MultiFactorAuthMethodForm';
import {
  Button,
  BodyText,
  HeaderText,
  Stack,
  TextFormField,
} from '../../../ui';
import { SuccessBanner } from '../../../consumer/booking/BookingFlow/SignInAndSignUp/SuccessBanner';

type MultiFactorAuthErrorType = {
  error?: string;
  mfa_status?: MultiFactorAuthStatus;
  errorCode?: number;
  errorMessage?: string;
};
export interface IMultiFactorAuthFormProps {
  mfaChoices: MultiFactorAuthChoiceType[];
  mfaMethod?: MultiFactorAuthMethod;
  isSubmitting?: boolean;
  errors?: MultiFactorAuthErrorType;
  onRequestCode?: (mfaMethod: MultiFactorAuthMethod, notify?: boolean, isResend?: boolean) => void;
  showNewCodeSent?: boolean;
  onVerify?: (mfaCode: string) => void;
}

// Statuses indicating that we need to issue a new MFA token
const MFA_TOKEN_INVALIDATED_STATUSES = [
  MultiFactorAuthStatus.Expired,
  MultiFactorAuthStatus.NoAttemptsLeft,
];

const RESEND_DELAY_MS = 60000;

const MultiFactorAuthForm: React.FCWithChildren<IMultiFactorAuthFormProps> = ({
  mfaChoices,
  mfaMethod: initialMfaMethod,
  isSubmitting = false,
  errors,
  onRequestCode,
  showNewCodeSent = false,
  onVerify,
}) => {
  const [mfaMethod, setMfaMethod] = useState<MultiFactorAuthMethod | null>(initialMfaMethod);
  const [verifyingMFA, setVerifyingMFA] = useState(false);
  const [code, setCode] = useState('');
  const [resendReady, setResendReady] = useState(false);
  const timeoutRef = useRef(null);

  // Analytics on mount
  useEffect(() => {
    analytics.track('client_loginpage_verifymfa_viewed', {
      options: mfaChoices.map(([choice]) => choice),
      method: mfaMethod,
    });
  }, []);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setResendReady(true);
    }, RESEND_DELAY_MS);

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleChangeCode = value => {
    setCode(value.replace(/[^\d]/g, ''));
  };

  const handleRequestCode = (method: MultiFactorAuthMethod) => {
    analytics.track('client_loginpage_sendcode_request_clicked', { method });
    setMfaMethod(method);
    onRequestCode(method, false);
  };

  const handleSendAgain = () => {
    if (!resendReady) {
      analytics.track('client_loginpage_resendmfa_clicked_disabled', { method: mfaMethod });
      return;
    }

    analytics.track('client_loginpage_resendmfa_clicked', { method: mfaMethod });
    analytics.track('signin_and_signup_mfa_requested');
    setCode('');
    setResendReady(false);
    timeoutRef.current = setTimeout(() => {
      setResendReady(true);
    }, RESEND_DELAY_MS);
    onRequestCode(mfaMethod, true, true);
  };

  const handleTryAnotherOption = () => {
    analytics.track('client_loginpage_tryanothermfa_clicked', { method: mfaMethod });
    setMfaMethod(null);
    setCode('');
  };

  const handleVerify = async () => {
    await analytics.track('client_loginpage_verifymfa_clicked', { method: mfaMethod });
    setVerifyingMFA(true);
    await onVerify(code);
    setTimeout(() => {
      setVerifyingMFA(false);
    }, 1000);
  };

  const generalError = errors?.error || errors?.errorMessage || errors?.toString?.();
  const isTokenExpired = MFA_TOKEN_INVALIDATED_STATUSES.includes(errors?.mfa_status);
  const isFormValid = !isSubmitting && !isTokenExpired && code.length === 6;
  // Extract the second value of the mfaOptions tuple: [[mfaMethod, deliverTo], ...]
  const sentTo = mfaChoices.find(([method]) => method === mfaMethod)?.slice(-1).pop();

  return (
    <Box
      flexDirection="column"
      flexGrow={1}
      paddingBottom={8}
      testID="multi-factor-auth-form"
    >
      {!mfaMethod && (
        <MultiFactorAuthMethodForm mfaChoices={mfaChoices} onSubmit={handleRequestCode} />
      )}

      {mfaMethod && (
        <Stack space={16} testID="multi-factor-auth-verify-form">
          <HeaderText level={1}>
            Enter your verification code
          </HeaderText>
          <BodyText color={BrandColor.Metal}>
            A verification code was sent over {MultiFactorAuthMethod[mfaMethod].toLowerCase()} to
            {' '}
            <BodyText style={{ whiteSpace: 'nowrap' }}>{sentTo}</BodyText>. Please enter the code to sign in.
          </BodyText>
          <Box paddingTop={8}>
            <TextFormField
              name="mfaCode"
              id="mfaCode"
              testID="mfaCode"
              type="shortcode"
              label="Verification Code"
              aria-label="Verification Code"
              characterLimit={6}
              value={code}
              message={generalError}
              disabled={isTokenExpired}
              onChangeText={handleChangeCode}
              borderColor={BrandColor.Silver}
              accentColor={BrandColor.Red}
              style={{
                borderRadius: 4,
                ...isTokenExpired && { backgroundColor: BrandColor.LightGrey },
              }}
            />
          </Box>
          {!showNewCodeSent && (
            <Box>
              <BodyText>
                It may take a minute to receive your code.
              </BodyText>
              <Inline>
                <BodyText>
                  Haven&lsquo;t received it?
                  {' '}
                </BodyText>
                <Pressable onPress={handleSendAgain}>
                  <BodyText
                    style={{ fontWeight: 'bold' }}
                    color={BrandColor.DarkBlue}
                    textDecoration="underline"
                  >
                    Resend a new code
                  </BodyText>
                </Pressable>
              </Inline>
            </Box>
          )}
          {showNewCodeSent && (
            <SuccessBanner
              successText="New code sent"
            />
          )}
          <Box paddingTop={24}>
            <Button
              appearance="primary-neue"
              disabled={!isFormValid || verifyingMFA}
              onClick={handleVerify}
            >
              {verifyingMFA ? 'Verifying' : 'Verify'}
            </Button>
          </Box>
          <Box paddingTop={8} alignItems="center">
            <Button appearance="link-lowercase" onClick={handleTryAnotherOption}>
              Try another option
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  );
};

MultiFactorAuthForm.defaultProps = {
  errors: undefined,
  onRequestCode: undefined,
  onVerify: undefined,
};

export default MultiFactorAuthForm;
